import React, { useCallback, useEffect, useState, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { useCurrentUser } from '../../../hooks/useCurrentUser'
import useApi from '../../../hooks/useApi'
import SignUpButton from '../../../components/Event/signUpButton'
import VolunteerListModal from './VolunteerListModal'
import VolunteerRow from './VolunteerRow'
import Loading from '../../../components/loading'
import styles from './volunteerList.module.scss'

export default function VolunteerSignUpSidebar({ event }) {
  const { t } = useTranslation()
  const { currentUser } = useCurrentUser()
  const { volunteerGet, getUserFollowing } = useApi()

  const [volunteers, setVolunteers] = useState([])
  const [followingIds, setFollowingIds] = useState(new Set())
  const [showModal, setShowModal] = useState(false)
  const [loading, setLoading] = useState(true)
  const hasFetched = useRef(false)
  const hasFetchedFollowingRef = useRef(false)

  const isOwner = useMemo(
    () => currentUser?.id === event?.user?.id,
    [currentUser?.id, event?.user?.id],
  )
  const isEventAdmin = useMemo(
    () => event?.admins?.some(a => a.id === currentUser?.id) || false,
    [event?.admins, currentUser?.id],
  )
  const canView = useMemo(
    () => isOwner || isEventAdmin || (event?.volunteers_needed || 0) > 0,
    [isOwner, isEventAdmin, event?.volunteers_needed],
  )

  // safety fallback for displaying volunteers
  const fallbackVolunteersFromTasks = useCallback(() => {
    const tasks = event?.tasks
    if (!Array.isArray(tasks)) return []

    const all = []
    tasks.forEach(task => {
      if (!Array.isArray(task?.shifts)) return
      task.shifts.forEach(shift => {
        if (!Array.isArray(shift?.volunteers)) return
        shift.volunteers.forEach(v => {
          if (v?.user?.id) all.push(v)
        })
      })
    })

    const seen = new Set()
    return all.filter(v => {
      const id = v.user?.id
      if (!id || seen.has(id)) return false
      seen.add(id)
      return true
    })
  }, [event?.tasks])

  useEffect(() => {
    hasFetched.current = false

    if (!event?.id) {
      setLoading(false)
      setVolunteers([])
      return
    }

    if (!canView) {
      setLoading(false)
      setVolunteers([])
      return
    }

    let cancelled = false

    const fetchVolunteers = async () => {
      setLoading(true)
      try {
        let followingPromise = Promise.resolve(null)
        if (currentUser?.id && !hasFetchedFollowingRef.current) {
          hasFetchedFollowingRef.current = true
          followingPromise = getUserFollowing({ id: currentUser.id }).catch(() => null)
        }
        
        const [volunteersResponse, followingResponse] = await Promise.all([
          volunteerGet({ id: event.id }),
          followingPromise,
        ])

        if (!cancelled) {
          const fromApi = Array.isArray(volunteersResponse?.data)
            ? volunteersResponse.data
            : []
          const fromTasks = fallbackVolunteersFromTasks()

          const source = fromApi.length ? fromApi : fromTasks

          if (source.length) {
            const sortedVolunteers = [...source].sort((a, b) => {
              const nameA = a.user?.name || ''
              const nameB = b.user?.name || ''
              return nameA.localeCompare(nameB)
            })
            setVolunteers(sortedVolunteers)
          } else {
            setVolunteers([])
          }

          if (followingResponse?.data) {
            const ids = new Set(followingResponse.data.map(user => user.id))
            setFollowingIds(ids)
          }
        }
      } catch (error) {
        if (!cancelled) {
          console.warn('Could not fetch volunteers:', error)
          const fromTasks = fallbackVolunteersFromTasks()
          setVolunteers(fromTasks)
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    fetchVolunteers()

    return () => {
      cancelled = true
    }
  }, [event?.id, currentUser?.id, canView])

  const handleOpenModal = useCallback(() => {
    setShowModal(true)
  }, [])

  const handleCloseModal = useCallback(async () => {
    setShowModal(false)
    if (event?.id && canView) {
      try {
        const [volunteersResponse, followingResponse] = await Promise.all([
          volunteerGet({ id: event.id }),
          currentUser?.id ? getUserFollowing({ id: currentUser.id }).catch(() => null) : Promise.resolve(null),
        ])

        if (volunteersResponse?.data) {
          const sortedVolunteers = [...volunteersResponse.data].sort((a, b) => {
            const nameA = a.user?.name || ''
            const nameB = b.user?.name || ''
            return nameA.localeCompare(nameB)
          })
          setVolunteers(sortedVolunteers)
        }

        if (followingResponse?.data) {
          const ids = new Set(followingResponse.data.map(user => user.id))
          setFollowingIds(ids)
        }
      } catch (error) {
        console.warn('Could not fetch volunteers:', error)
      }
    }
  }, [event?.id, canView, currentUser?.id])

  const handleUpdate = useCallback((updatedVolunteer) => {
    if (event?.id && canView) {
      if (updatedVolunteer) {
        setVolunteers(prevVolunteers => {
          const updated = prevVolunteers.map(v => {
            if (v.id === updatedVolunteer.id || v.user?.id === updatedVolunteer.user?.id) {
              return updatedVolunteer
            }
            return v
          })
          return [...updated].sort((a, b) => {
            const nameA = a.user?.name || ''
            const nameB = b.user?.name || ''
            return nameA.localeCompare(nameB)
          })
        })
      } else {
        const fetchData = async () => {
          try {
            const [volunteersResponse, followingResponse] = await Promise.all([
              volunteerGet({ id: event.id }),
              currentUser?.id ? getUserFollowing({ id: currentUser.id }).catch(() => null) : Promise.resolve(null),
            ])

            if (volunteersResponse?.data) {
              const sortedVolunteers = [...volunteersResponse.data].sort((a, b) => {
                const nameA = a.user?.name || ''
                const nameB = b.user?.name || ''
                return nameA.localeCompare(nameB)
              })
              setVolunteers(sortedVolunteers)
            }

            if (followingResponse?.data) {
              const ids = new Set(followingResponse.data.map(user => user.id))
              setFollowingIds(ids)
            }
          } catch (error) {
            console.warn('Could not fetch volunteers:', error)
          }
        }
        fetchData()
      }
    }
  }, [event?.id, canView, currentUser?.id, volunteerGet, getUserFollowing])


  const uniqueVolunteers = useMemo(() => {
    const seen = new Set()
    const unique = []
    for (const volunteer of volunteers) {
      const userId = volunteer.user?.id
      if (userId && !seen.has(userId)) {
        seen.add(userId)
        unique.push(volunteer)
      }
    }
    return unique
  }, [volunteers])

  if (!canView) {
    return null
  }

  const volunteerCount = uniqueVolunteers.length
  const maxVolunteers = event.volunteers_needed || 0
  const previewVolunteers = uniqueVolunteers.slice(0, 5)

  return (
    <>
      <div className={styles.sidebar}>
        <div className={styles.sidebarHeader}>
          <h5 className={styles.sidebarTitle}>
            {t('VolunteerListScreen.volunteer_label', 'Volunteer Sign Up')}
          </h5>
          <span className={styles.sidebarCount}>
            {t('VolunteerListScreen.count_label', '{{count}} of {{total}}', {
              count: volunteerCount,
              total: maxVolunteers || '∞',
            })}
          </span>
        </div>

        <Loading show={loading} />
        {!loading && volunteerCount > 0 ? (
          <>
            <div className={styles.previewList}>
              {previewVolunteers.map(volunteer => {
                const enrichedVolunteer = {
                  ...volunteer,
                  user: {
                    ...volunteer.user,
                    current_user_follows: volunteer.user?.current_user_follows !== undefined
                      ? volunteer.user.current_user_follows
                      : followingIds.has(volunteer.user?.id),
                  },
                }
                return (
                  <VolunteerRow
                    key={volunteer.id}
                    volunteer={enrichedVolunteer}
                    event={event}
                    onUpdate={handleUpdate}
                    showCheckInButton={false}
                  />
                )
              })}
            </div>
            {volunteerCount > 5 && (
              <div className={styles.moreVolunteers}>
                +{volunteerCount - 5}{' '}
                {volunteerCount - 5 === 1 ? 'volunteer' : 'volunteers'}
              </div>
            )}
          </>
        ) : !loading ? (
          <p className={styles.emptyMessage}>
            {t('VolunteerListScreen.volunteer_label', 'No volunteers yet')}
          </p>
        ) : null}

        <button
          className={styles.viewAllButton}
          onClick={handleOpenModal}
          type="button"
        >
          {t('VolunteerListScreen.volunteer_label', 'View List')}
        </button>

        <div className={styles.signUpButtonContainer}>
          <SignUpButton event={event} />
        </div>
      </div>

      <VolunteerListModal
        event={event}
        onHide={handleCloseModal}
        show={showModal}
      />
    </>
  )
}


